import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { Select, DatePicker, Tooltip, Input, InputNumber, StatCard, Counted, Tag, FilterResetButton } from '../../ui';
import { CodeChip } from '../../ui/CodeChip';
import { EntityAvatar, entityHue } from '../../ui/EntityAvatar';
import { PALETTE, TAG_TONE_HUE, filterTint } from '../../lib/palette';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import { useUrlString, useUrlList, useUrlDateRange, useUrlNumberOrNull } from '../../hooks/useUrlState';
import { Eye, Search, Clock, Truck, BadgeIndianRupee, Ban, Undo2 } from 'lucide-react';
import type { Dayjs } from 'dayjs';
import { disableFuture } from '../../lib/dateRange';
import { request } from '../../api/client';
import { usePermission } from '../../store/auth';
import { useDebounce } from '../../hooks/useDebounce';
import { useServerSort } from '../../hooks/useServerSort';
import { TwDataTable, Pill, type DataTableColumn } from '../../components/TwDataTable';
import { RoleBadge, roleTone } from '../../components/RoleBadge';
import { RetailerLink, OrderLink, UserLink, ReturnLink } from '../../components/EntityLinks';
import { TerritoryChips } from '../../components/TerritoryChips';
import { Button } from '../../ui/Button';
import { Modal, message } from '../../ui';
import { formatIst } from '../../lib/time';
import { fmtQty, inr2 } from '../../lib/format';
import { ReturnDetailPanel, REASON_META } from './ReturnDetailPage';

/** Decided states a return can be sent back FROM — mirrors the `return` lane's revert spec. */
const REVERTIBLE = new Set(['APPROVED', 'REJECTED']);

// Lifecycle status → pill colour.
const STATUS_PILL: Record<string, string> = {
  REQUESTED: 'blue', APPROVED: 'lime', IN_TRANSIT: 'gold', RECEIVED: 'purple',
  CREDITED: 'green', REJECTED: 'red', CANCELLED: 'default', POSTED: 'green',
};
/**
 * The COLOUR VALUE behind each of those pills, for the places that need a colour and not a class —
 * the tint an active filter wears, the chip a chosen status leaves in the picker. Same order of
 * meaning as `STATUS_PILL` above: a status is one colour on this page, wherever it is drawn.
 */
const STATUS_HUE: Record<string, string> = {
  REQUESTED: PALETTE.sky[500], APPROVED: PALETTE.brand[300], IN_TRANSIT: PALETTE.harvest[500],
  RECEIVED: PALETTE.plum[500], CREDITED: PALETTE.brand[600], REJECTED: PALETTE.danger[500],
  CANCELLED: PALETTE.earth[400], POSTED: PALETTE.brand[600],
};
const STATUSES = ['REQUESTED', 'APPROVED', 'IN_TRANSIT', 'RECEIVED', 'CREDITED', 'REJECTED', 'CANCELLED'];
const REASONS = [
  'DAMAGED_IN_TRANSIT', 'DEFECTIVE', 'WRONG_ITEM', 'EXPIRED_ON_DELIVERY',
  'NOT_NEEDED', 'ORDERED_BY_MISTAKE', 'CHANGED_MIND', 'OTHER',
];
/**
 * `POSTED` is where the legacy direct-post return ends up — goods back in and the credit note raised
 * in one step, the same end state the lifecycle calls CREDITED. Two words for one state is how the
 * "Credit issued" card came to count 11 returns and open 6, so the register speaks one: the server
 * expands a CREDITED filter over both (RETURN_STATUS_ALIASES), and the pill reads Credited here.
 */
const STATUS_ALIAS: Record<string, string> = { POSTED: 'CREDITED' };
const statusLabel = (s: string) => String(STATUS_ALIAS[s] ?? s ?? '').replace(/_/g, ' ');
const reasonLabel = (s?: string) => (s ?? '').replace(/_/g, ' ').toLowerCase();
/** Sentence case for a filter option — "Damaged in transit" reads better in a list than a SHOUTED code. */
const titleCase = (s: string) => {
  const t = s.replace(/_/g, ' ').toLowerCase();
  return t.charAt(0).toUpperCase() + t.slice(1);
};

/**
 * The KPI cards, which double as the status filter.
 *
 * Each card owns a set of statuses; clicking it selects exactly those, clicking it again clears the
 * filter. They are counted server-side over the whole filtered set (minus the status filter itself),
 * so a card never disagrees with the rows under it and never reads zero just because another card
 * is selected.
 */
const CARDS = [
  { key: 'pending', label: 'Waiting for approval', statuses: ['REQUESTED'], tone: 'warning' as const, icon: <Clock size={16} /> },
  // "In progress" named no fact — every one of these is approved and none is credited yet, which is
  // the thing a reader is actually asking when they look at this tile.
  { key: 'progress', label: 'Approved, not yet credited', statuses: ['APPROVED', 'IN_TRANSIT', 'RECEIVED'], tone: 'sky' as const, icon: <Truck size={16} /> },
  // 'CREDITED' reaches the legacy POSTED returns too — the server expands it (RETURN_STATUS_ALIASES),
  // so this card counts and opens exactly the same returns.
  { key: 'credited', label: 'Credit issued', statuses: ['CREDITED'], tone: 'success' as const, icon: <BadgeIndianRupee size={16} /> },
  { key: 'closed', label: 'Closed without credit', statuses: ['REJECTED', 'CANCELLED'], tone: 'danger' as const, icon: <Ban size={16} /> },
];

const sameSet = (a: string[], b: string[]) => a.length === b.length && a.every((x) => b.includes(x));

/** Three of the four tiles count RETURNS, and each says so on its own face — see `Counted`. */
const returnsCount = (n: any) => <Counted n={n} one="return" many="returns" />;

/** "8 days", not "8d" — a card is read by a person, and the band has room for the word. */
const days = (n: any) => `${Number(n ?? 0)} day${Number(n ?? 0) === 1 ? '' : 's'}`;

/** What a card says instead of a number it could not load. "0" would be a lie nobody can spot. */
const FAILED = 'could not load the totals';

/** Hairline between filter groups. Hidden once the toolbar wraps, where a vertical rule between two
 *  controls on different lines would separate the wrong pair. */
const FilterRule = () => <span className="hidden h-6 w-px shrink-0 bg-line xl:block" aria-hidden />;

export function ReturnsListPage() {
  const [sp] = useSearchParams();
  const { id: routeId } = useParams(); // deep-link /returns/:id → open the drawer for that return
  const navigate = useNavigate();

  // ── Filters, in the same order as the columns they sit under ──
  const [returnNoInput, setReturnNoInput] = useState('');
  const [orderNoInput, setOrderNoInput] = useState('');
  const [retailerId, setRetailerId] = useUrlString<string | undefined>('retailer', undefined);
  const [status, setStatus] = useUrlList('status');
  const [qtyMin, setQtyMin] = useUrlNumberOrNull('qtyMin');
  const [qtyMax, setQtyMax] = useUrlNumberOrNull('qtyMax');
  const [reasonCode, setReasonCode] = useUrlList('reason');
  const [requestedBy, setRequestedBy] = useUrlString<string | undefined>('by', undefined);
  const [range, setRange] = useUrlDateRange('range');

  const returnNo = useDebounce(returnNoInput, 400);
  const orderNo = useDebounce(orderNoInput, 400);
  const qtyMinD = useDebounce(qtyMin, 400);
  const qtyMaxD = useDebounce(qtyMax, 400);

  const [openId, setOpenId] = useState<string | null>(null); // right-side detail drawer
  /*
   * A notification deep-link can carry ?status=… — and `status` now READS that param directly, so the
   * deep link is honoured with no effect at all.
   *
   * The effect that used to do it here had to go: it re-parsed the param as a single value, so once
   * the filter became multi-select, choosing two statuses wrote `status=APPROVED,RECEIVED` and the
   * effect immediately fed that whole string back as ONE bogus status, wiping the real selection.
   */
  useEffect(() => { if (routeId) setOpenId(routeId); }, [routeId]);

  // Closing the drawer clears the selection; if we arrived via a deep-link, drop back to the clean /returns
  // URL (replace, so Back doesn't re-open it) so the same link can be followed again later.
  const qc = useQueryClient();
  const closeDrawer = () => { setOpenId(null); if (routeId) navigate('/returns', { replace: true }); };

  // "Reopen this" — Accounts/Admin raise it, a Super Admin decides. The statuses match the return
  // lane's own revert spec: only a decided return can be sent back.
  const canRequestRevert = usePermission('APPROVAL.REVERT.REQUEST');
  const [revertFor, setRevertFor] = useState<any | null>(null);
  const [revertReason, setRevertReason] = useState('');
  const requestRevert = useConfirmedMutation({ title: "Request a reversal of this return?", content: "It goes to a Super Admin to decide. Nothing changes until they approve it.", okText: "Yes, request it" }, {
    mutationFn: () => request({
      url: `/approvals/return/${revertFor.id}/revert-request`,
      method: 'POST',
      data: { reason: revertReason.trim() },
    }),
    onSuccess: () => {
      message.success('Sent to Approvals — a Super Admin will decide');
      setRevertFor(null); setRevertReason('');
      qc.invalidateQueries({ queryKey: ['approvals'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Could not raise the request'),
  });

  // Server-side column sort (backend ORDER BY over the full result set).
  const { sortState, onSortChange, sort } = useServerSort();

  /** Everything except status + sort — the shape the KPI band is counted over. */
  const baseParams = useMemo(() => {
    const p: Record<string, any> = {};
    if (returnNo.trim()) p.search = returnNo.trim();
    if (orderNo.trim()) p.orderNo = orderNo.trim();
    if (retailerId) p.retailerId = retailerId;
    if (qtyMinD != null) p.qtyMin = qtyMinD;
    if (qtyMaxD != null) p.qtyMax = qtyMaxD;
    if (reasonCode.length) p.reasonCode = reasonCode.join(',');
    if (requestedBy) p.requestedBy = requestedBy;
    if (range[0]) p.from = range[0].format('YYYY-MM-DD');
    if (range[1]) p.to = range[1].format('YYYY-MM-DD');
    return p;
  }, [returnNo, orderNo, retailerId, qtyMinD, qtyMaxD, reasonCode, requestedBy, range]);

  const listParams = useMemo(() => {
    const p = { ...baseParams };
    if (status.length) p.status = status.join(',');
    if (sort) p.sort = sort;
    return p;
  }, [baseParams, status, sort]);

  const { data, isLoading } = useQuery({
    queryKey: ['returns', listParams],
    queryFn: () => request<any[]>({ url: '/returns', params: listParams }),
    placeholderData: keepPreviousData,
  });
  const rows = Array.isArray(data) ? data : [];

  const { data: meta, isError: metaFailed, isPending: metaLoading } = useQuery({
    queryKey: ['returns-summary', baseParams],
    queryFn: () => request<any>({ url: '/returns/summary', params: baseParams }),
    placeholderData: keepPreviousData,
  });
  const s = meta?.summary ?? {};
  const facets = meta?.facets ?? {};
  // A card that prints "0" when it actually means "I could not load this" is a lie the reader has no
  // way to spot — an em-dash says the number is missing, which is the truth.
  const unknown = metaFailed || (metaLoading && !meta);

  /*
   * ── THE PICKERS ARE MADE OF THE MARKS THE ROWS ARE MADE OF ──
   *
   * Every control on this bar was a column of identical grey sentences: "R-000001 · Demo Retailer",
   * "Damaged in transit", "Received". Two problems, both the same problem.
   *
   * A retailer CODE is an identifier — the half you scan when two dealers share a name — and a dot
   * is weaker than the spaces around it, so the code dissolved into the name. It is a chip now, in
   * that dealer's own identity hue, beside their mark; the name is the only part allowed to give.
   *
   * A status and a return reason already HAVE a colour: the pill on the row and the reason tag in
   * the detail panel. Printing them as grey text in the picker made choosing one a different act
   * from reading one. The option is now the same tag the row wears, so you pick the thing you are
   * about to be looking at — and the reason's colour is not decoration, it is whose fault the
   * return is (red/gold = seller or carrier, blue = the buyer's own choice), which is what decides
   * whether it gets credited in full.
   */
  const retailerOpts = useMemo(() => (facets.retailers ?? []).map((r: any) => {
    const name = String(r.name ?? '').trim() || `#${r.id}`;
    const code = String(r.code ?? '').trim();
    return {
      value: String(r.id),
      // A node label has no text to search — carry the plain twin, or typing a dealer code matches
      // nothing at all (the Select is pointed at `searchLabel`).
      searchLabel: `${name} ${code}`.trim(),
      title: [name, code].filter(Boolean).join(' — '),
      // The collapsed trigger is one row high: mark + name. The code chip makes the LIST scannable;
      // in the trigger it is width, and width is what clips the name.
      selectedText: (
        <span className="flex min-w-0 items-center gap-1.5">
          <EntityAvatar name={name} size={18} />
          <span className="min-w-0 truncate">{name}</span>
        </span>
      ),
      label: (
        <span className="flex min-w-0 items-center gap-2">
          <EntityAvatar name={name} size={24} />
          <span className="min-w-26 flex-1 truncate">{name}</span>
          {code ? <CodeChip hue={entityHue(code)} className="min-w-20 shrink-0 text-center">{code}</CodeChip> : null}
        </span>
      ),
    };
  }), [facets.retailers]);

  const statusOpts = useMemo(() => (facets.statuses ?? STATUSES).map((v: string) => ({
    value: v,
    title: titleCase(v),
    chipHue: STATUS_HUE[v],
    selectedText: titleCase(v),
    label: <Pill color={STATUS_PILL[v] ?? 'default'}>{titleCase(v)}</Pill>,
  })), [facets.statuses]);

  const reasonOpts = useMemo(() => (facets.reasonCodes ?? REASONS).map((v: string) => {
    const m = REASON_META[v];
    const text = m?.label ?? titleCase(v);
    return {
      value: v,
      title: text,
      chipHue: TAG_TONE_HUE[m?.tone ?? 'neutral'],
      selectedText: text,
      fault: m?.fault ?? null,
      label: <Tag tone={m?.tone ?? 'neutral'}>{text}</Tag>,
    };
  }), [facets.reasonCodes]);

  // The hue each set control wears — its own subject's colour, the one that value already has in the
  // rows below. A multi-select takes the first value it holds; the rest are chips on the control.
  const retailerHue = useMemo(() => {
    const r = (facets.retailers ?? []).find((x: any) => String(x.id) === String(retailerId));
    return r ? entityHue(String(r.code ?? r.name ?? '')) : null;
  }, [facets.retailers, retailerId]);
  const raisedByHue = useMemo(() => {
    const u = (facets.raisedBy ?? []).find((x: any) => String(x.id) === String(requestedBy));
    return u ? TAG_TONE_HUE[roleTone(u.role)] ?? null : null;
  }, [facets.raisedBy, requestedBy]);

  const filtersActive =
    Object.keys(baseParams).length > 0 || status.length > 0 || returnNoInput !== '' || orderNoInput !== '';
  const clearAll = () => {
    setReturnNoInput(''); setOrderNoInput(''); setRetailerId(undefined); setStatus([]);
    setQtyMin(null); setQtyMax(null); setReasonCode([]); setRequestedBy(undefined); setRange([null, null]);
  };

  /** Clicking a card selects its statuses; clicking the selected one clears the filter. */
  const toggleCard = (statuses: string[]) => setStatus((cur) => (sameSet(cur, statuses) ? [] : statuses));

  /*
   * THE BAND'S WORDING, to three rules that apply to every KPI band in the app:
   *
   *  1. The VALUE carries its own unit. A bare "1" over "Waiting for approval" is one WHAT — and the
   *     unit cannot live on whichever neighbouring tile happened to mention it.
   *  2. The LABEL states the fact the bucket is true of, not the name of a stage.
   *  3. The HINT holds one kind of thing. Every one of these is a fact about what is INSIDE the
   *     figure above it — never a second subject smuggled in beside the first.
   *
   * Rule 3 is why the pending tile no longer reads "1 unit · oldest waiting 8d": that put a count of
   * GOODS next to an age, on a tile whose own figure counts RETURNS — three subjects, one line. The
   * quantity is a column on the rows the tile opens, which is where it can be read against a product.
   */
  const cardValue: Record<string, { value: ReactNode; hint: string }> = {
    pending: {
      value: unknown ? '—' : returnsCount(s.requested),
      hint: unknown ? FAILED
        : !s.requested ? 'nothing waiting'
          : s.oldestPendingDays ? `oldest has waited ${days(s.oldestPendingDays)}`
            : 'all raised today',
    },
    progress: {
      value: unknown ? '—' : returnsCount(s.inProgress),
      // The three stages of one journey, named as where the goods ARE — "approved · in transit ·
      // received" repeated the status column at a reader who is looking at this tile precisely
      // because they do not want to read statuses.
      hint: unknown ? FAILED
        : s.inProgress
          ? `${s.approved ?? 0} awaiting pickup · ${s.inTransit ?? 0} coming back · ${s.received ?? 0} arrived`
          : 'nothing waiting to be credited',
    },
    credited: {
      // The one tile whose figure is MONEY — what Accounts actually posted, reversed notes excluded.
      // Its hint therefore has to say how many returns that money covers, or the count it drills to
      // looks like it came from nowhere. Both sides count CREDITED and legacy POSTED alike.
      value: unknown ? '—' : inr2(s.creditIssued ?? 0),
      hint: unknown ? FAILED
        : s.credited
          ? `across ${s.credited} return${s.credited === 1 ? '' : 's'}`
          : 'no credit issued yet',
    },
    closed: {
      value: unknown ? '—' : returnsCount(s.closedNoCredit),
      hint: unknown ? FAILED
        : s.closedNoCredit
          ? `${s.rejected ?? 0} rejected · ${s.cancelled ?? 0} cancelled`
          : 'nothing rejected or cancelled',
    },
  };

  const columns: DataTableColumn<any>[] = [
    /*
     * The three identifiers in this register are DOORS, not text: the return itself, the order it
     * came off, and the person who raised it. Each opens the record it names — the register's own
     * drawer for the return, the order page, the staff record — so following the trail out of a row
     * never means memorising a number and going to find it in another list.
     */
    {
      key: 'returnNo', title: 'Return No', dataIndex: 'returnNo', sortable: true, alwaysVisible: true, width: 160,
      render: (v: string, r: any) => <ReturnLink id={r.id}>{v ?? `#${r.id}`}</ReturnLink>,
    },
    {
      key: 'orderNo', title: 'Order', dataIndex: 'orderNo', sortable: true, width: 150,
      render: (v: string, r: any) => (v ? <OrderLink id={r.orderId}>{v}</OrderLink> : '—'),
    },
    {
      key: 'retailer', title: 'Retailer', alwaysVisible: true, sortable: true, width: 390,
      render: (_: any, r: any) => (
        <div className="min-w-0">
          {/* Name and dealer code on one line (both are identity); the address on the next. Still
              TWO lines, not three — a line added to one cell is a line added to every row of the
              register, and these tables already scroll sideways, which is the cheap direction. */}
          <div className="flex min-w-0 items-baseline gap-1.5">
            <span className="truncate font-semibold"><RetailerLink id={r.retailerId}>{r.companyName ?? `#${r.retailerId}`}</RetailerLink></span>
            {r.retailerCode && <span className="shrink-0 text-xs text-subtle">{r.retailerCode}</span>}
          </div>
          {/* `wrap`: a full state → district → beat → town chain is wider than the column, and an
              unwrapped chain drew straight over the Status pill. It wraps only when it cannot fit. */}
          <TerritoryChips territoryId={r.retailerTerritoryId} city={r.retailerCity} hideWhenEmpty wrap />
        </div>
      ),
    },
    { key: 'status', title: 'Status', dataIndex: 'status', sortable: true, width: 120, render: (v: string) => <Pill color={STATUS_PILL[v] ?? 'default'}>{statusLabel(v)}</Pill> },
    { key: 'requestedQty', title: 'Qty', dataIndex: 'requestedQty', sortable: true, width: 90, render: (v: any) => fmtQty(v) },
    /* The reason is the same tag here, in the picker above and in the detail panel — one vocabulary,
       one colour. A legacy return stored free text rather than a code; that falls through to plain
       words with no fault claimed, because we genuinely do not know which it was. */
    { key: 'reasonCode', title: 'Reason', dataIndex: 'reasonCode', sortable: true, width: 170, defaultVisible: true,
      render: (v: string) => {
        const m = REASON_META[v];
        if (m) return <Tag tone={m.tone}>{m.label}</Tag>;
        return <span className="capitalize text-muted">{reasonLabel(v) || '—'}</span>;
      } },
    {
      key: 'requestedBy', title: 'Raised by', dataIndex: 'requestedByName', sortable: true, width: 215, defaultVisible: true,
      // Name + role pill: a return raised by the dealer, by their Sales Person or by an Admin on
      // someone's behalf reads the same as a bare name and means three different things.
      render: (v: string, r: any) => (r.requestedBy ? (
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="min-w-0 truncate">
            <UserLink id={r.requestedBy} role={r.requestedByRole}>{v ?? `#${r.requestedBy}`}</UserLink>
          </span>
          <RoleBadge role={r.requestedByRole} />
        </span>
      ) : <span className="text-muted">—</span>),
    },
    { key: 'createdAt', title: 'Raised on', dataIndex: 'createdAt', sortable: true, width: 185, render: (v: string) => <span className="whitespace-nowrap">{formatIst(v)}</span> },
    { key: 'totalAmount', title: 'Value', dataIndex: 'totalAmount', width: 120, align: 'right', sortable: true,
      render: (v: any) => (Number(v) > 0 ? inr2(v) : <span className="text-subtle">—</span>) },
    { key: 'receivedQty', title: 'Received', dataIndex: 'receivedQty', width: 110, align: 'right',
      // Requested vs received is the whole point of the inspection step — a short receipt is the
      // thing someone needs to notice, so it is called out rather than left to the detail view.
      render: (v: any, r: any) => {
        const got = Number(v ?? 0), want = Number(r.requestedQty ?? 0);
        if (!got) return <span className="text-subtle">—</span>;
        return got < want
          ? <Tooltip label={`${fmtQty(got)} of ${fmtQty(want)} received`}><span className="text-warning-600 dark:text-harvest-300">{fmtQty(got)}</span></Tooltip>
          : <span>{fmtQty(got)}</span>;
      } },
    { key: 'lineCount', title: 'Products', dataIndex: 'lineCount', width: 80, align: 'right', defaultVisible: false,
      render: (v: any) => Number(v ?? 0) || <span className="text-subtle">—</span> },
    { key: 'warehouseName', title: 'Warehouse', dataIndex: 'warehouseName', width: 160, ellipsis: true, sortable: true,
      render: (v: string) => v || <span className="text-subtle">—</span> },
    { key: 'orderStatus', title: 'Order status', dataIndex: 'orderStatus', width: 130, defaultVisible: false,
      render: (v: string) => (v ? <Pill color="gray">{v}</Pill> : <span className="text-subtle">—</span>) },
    { key: 'invoiceNo', title: 'Invoice', dataIndex: 'invoiceNo', width: 150, defaultVisible: false,
      render: (v: string) => v || <span className="text-subtle">—</span> },
    { key: 'reasonNote', title: 'Reason note', dataIndex: 'reasonNote', width: 200, ellipsis: true, defaultVisible: false,
      render: (v: string) => v || <span className="text-subtle">—</span> },
    { key: 'approvedByName', title: 'Approved by', dataIndex: 'approvedByName', width: 170, ellipsis: true, defaultVisible: false,
      render: (v: string, r: any) => (v
        ? <span>{v}<span className="block type-caption text-subtle">{r.approvedAt ? formatIst(r.approvedAt) : ''}</span></span>
        : <span className="text-subtle">—</span>) },
    { key: 'creditedByName', title: 'Credited by', dataIndex: 'creditedByName', width: 170, ellipsis: true, defaultVisible: false,
      render: (v: string, r: any) => (v
        ? <span>{v}<span className="block type-caption text-subtle">{r.creditedAt ? formatIst(r.creditedAt) : ''}</span></span>
        : <span className="text-subtle">—</span>) },
    { key: 'creditNoteNos', title: 'Credit note', dataIndex: 'creditNoteNos', width: 170, ellipsis: true,
      render: (v: string) => (v ? <span className="font-mono text-[12.5px]">{v}</span> : <span className="text-subtle">—</span>) },
    { key: 'returnDate', title: 'Return date', dataIndex: 'returnDate', width: 130, sortable: true, defaultVisible: false,
      render: (v: string) => (v ? String(v).substring(0, 10) : <span className="text-subtle">—</span>) },
    {
      key: 'actions', title: '', width: 104, align: 'right', alwaysVisible: true,
      render: (_: any, r: any) => (
        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          <Tooltip label="Open the return">
            <Button variant="text" size="sm" aria-label="Open the return" icon={<Eye className="size-4" />} onClick={() => setOpenId(String(r.id))} />
          </Tooltip>
          {/* Only offered on a DECIDED return, and only to someone who may ask (Accounts/Admin).
              Asking is not reverting: it queues the question for a Super Admin. */}
          {/* The reversal action only applies to a DECIDED return, but the slot is held open on every
              row when the viewer can raise one — otherwise the eye slides right on the rows without
              it and the column reads as ragged. */}
          {canRequestRevert && (
            REVERTIBLE.has(String(r.status)) ? (
              <Tooltip label="Ask a Super Admin to reopen this">
                <Button variant="text" size="sm" aria-label="Request reversal"
                  icon={<Undo2 className="size-4" />} onClick={() => setRevertFor(r)} />
              </Tooltip>
            ) : (
              <span className="inline-block size-8 shrink-0" aria-hidden />
            )
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <h1 className="mb-3 type-page-title text-content">Returns</h1>

      {/* KPI band — the four states a return can be in, each one a filter. */}
      <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {CARDS.map((c) => (
          <StatCard
            key={c.key}
            label={c.label}
            value={cardValue[c.key].value}
            hint={cardValue[c.key].hint}
            tone={c.tone}
            icon={c.icon}
            active={sameSet(status, c.statuses)}
            onClick={() => toggleCard(c.statuses)}
          />
        ))}
      </div>

      <TwDataTable
        storageKey="returns-list"
        rowKey="id"
        layoutFixed
        loading={isLoading}
        dataSource={rows}
        columns={columns}
        sortState={sortState}
        onSortChange={onSortChange}
        onRow={(r) => ({ onClick: () => setOpenId(String(r.id)) })}
        pagination={{ pageSize: 25, showSizeChanger: true }}
        emptyText={filtersActive ? 'No returns match these filters' : 'No returns'}
        toolbarLeft={
          /*
           * One filter per column, in COLUMN ORDER: return no · order · retailer · status · qty ·
           * reason · raised by · raised on. Reading the toolbar left-to-right walks the table header
           * left-to-right, so there is never a control whose column you have to hunt for.
           *
           * They are GROUPED, not evenly spaced. An identical gap between all eight made the row read
           * as one undifferentiated wall — worst of all for the Qty pair, which is a single filter
           * that looked like two. Tight inside a group, a hairline between groups: find it by
           * document number, then by who and what state, then by who raised it and when.
           */
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            {/* ── Find a specific document ── */}
            <div className="flex flex-wrap items-center gap-1.5">
              <Input
                prefix={<Search size={16} />} placeholder="Return no…" aria-label="Return no" className="w-38" allowClear
                style={returnNoInput.trim() ? filterTint(PALETTE.sky[500]) : undefined}
                value={returnNoInput} onChange={(e) => setReturnNoInput(e.target.value)}
              />
              <Input
                prefix={<Search size={16} />} placeholder="Order no…" aria-label="Order no" className="w-38" allowClear
                style={orderNoInput.trim() ? filterTint(PALETTE.sky[500]) : undefined}
                value={orderNoInput} onChange={(e) => setOrderNoInput(e.target.value)}
              />
            </div>

            <FilterRule />

            {/* ── What the return is: whose, what state, how much, why ── */}
            <div className="flex flex-wrap items-center gap-1.5">
              <Select
                placeholder="Retailer" allowClear showSearch
                optionFilterProp="searchLabel" optionLabelProp="selectedText" popupMatchSelectWidth={false}
                style={{ width: 190, ...filterTint(retailerHue) }}
                value={retailerId} onChange={(v) => setRetailerId(v as string | undefined)}
                options={retailerOpts}
              />
              <Select
                mode="multiple" placeholder="Status" allowClear optionLabelProp="selectedText"
                style={{ width: 180, ...filterTint(status.length ? STATUS_HUE[status[0]] : null) }}
                value={status} onChange={(v) => setStatus((v as string[]) ?? [])}
                options={statusOpts}
              />
              {/*
               * ONE control, not two. The min/max pair sits inside its own shell with its label, so a
               * quantity band cannot be mistaken for two unrelated filters. Spinners are off: they
               * doubled the width and nobody steps a quantity threshold one unit at a time.
               */}
              <div className="inline-flex flex-wrap items-center gap-1.5 rounded-control border border-line bg-surface-2/50 py-1 pl-2.5 pr-1.5"
                style={filterTint(qtyMin != null || qtyMax != null ? PALETTE.sky[500] : null)}>
                <span className="type-label text-subtle">Qty</span>
                <InputNumber size="small" controls={false} placeholder="min" min={0} className="w-16"
                  value={qtyMin} onChange={(v) => setQtyMin(v as number | null)} />
                <span className="text-subtle">–</span>
                <InputNumber size="small" controls={false} placeholder="max" min={0} className="w-16"
                  value={qtyMax} onChange={(v) => setQtyMax(v as number | null)} />
              </div>
              <Select
                mode="multiple" placeholder="Reason" allowClear optionLabelProp="selectedText" popupMatchSelectWidth={false}
                style={{ width: 200, ...filterTint(reasonCode.length ? TAG_TONE_HUE[REASON_META[reasonCode[0]]?.tone ?? 'neutral'] : null) }}
                value={reasonCode} onChange={(v) => setReasonCode((v as string[]) ?? [])}
                options={reasonOpts}
                /* Whose fault the reason is decides whether the return is credited in full — it is
                   what the colour means, said in words beside it rather than left as a code only
                   Accounts knows how to read. */
                optionRender={(o: any) => (
                  <span className="flex w-full items-center justify-between gap-3">
                    {o.label}
                    {o.fault && (
                      <span className="shrink-0 type-caption text-subtle">
                        {o.fault === 'seller' ? 'seller / carrier' : 'buyer’s choice'}
                      </span>
                    )}
                  </span>
                )}
              />
            </div>

            <FilterRule />

            {/* ── Who raised it, and when ── */}
            <div className="flex flex-wrap items-center gap-1.5">
              <Select
                placeholder="Raised by" allowClear showSearch style={{ width: 170, ...filterTint(raisedByHue) }}
                optionFilterProp="name" optionLabelProp="name"
                value={requestedBy} onChange={(v) => setRequestedBy(v as string | undefined)}
                options={(facets.raisedBy ?? []).map((u: any) => ({
                  value: u.id, name: u.name, label: u.name, role: u.role ?? null,
                }))}
                /* A return raised by the dealer themselves, by their Sales Person and by an Admin on
                   someone's behalf are three different things, and a list of bare names says which is
                   which nowhere. The role pill is the app-wide answer to that (components/RoleBadge),
                   and it is the same role the register's own "Raised by" column shows. */
                optionRender={(o: any) => (
                  <span className="flex w-full items-center gap-2">
                    <span className="min-w-0 flex-1 truncate">{o.name}</span>
                    <RoleBadge role={o.role} />
                  </span>
                )}
              />
              {/*
                * TWO independent date fields, not a RangePicker.
                *
                * RangePicker deliberately emits only a COMPLETE range — a half-picked [start, null]
                * used to blank the pages that consume it, so it holds the draft until both ends are
                * chosen. That contract is right for those pages and wrong here: "everything raised
                * since Monday" is a one-sided question, and asking it through the range picker did
                * nothing at all — you picked a start date and the list never moved.
                *
                * The server applies each bound on its own, so the UI now offers each bound on its own.
                */}
              <div className="inline-flex flex-wrap items-center gap-1.5 rounded-control border border-line bg-surface-2/50 py-1 pl-2.5 pr-1.5"
                style={filterTint(range[0] || range[1] ? PALETTE.sky[500] : null)}>
                <span className="type-label text-subtle">Raised</span>
                <DatePicker
                  size="small" placeholder="from" allowClear disabledDate={disableFuture}
                  value={range[0]} maxDate={range[1] ?? undefined}
                  onChange={(d: any) => setRange(([, to]) => [d ?? null, to])}
                />
                <span className="text-subtle">–</span>
                <DatePicker
                  size="small" placeholder="to" allowClear disabledDate={disableFuture}
                  value={range[1]} minDate={range[0] ?? undefined}
                  onChange={(d: any) => setRange(([from]) => [from, d ?? null])}
                />
              </div>
            </div>

            {filtersActive && (
              <FilterResetButton onClick={clearAll} />
            )}
          </div>
        }
      />
      {rows.length >= 200 && (
        <div className="mt-2 type-caption text-subtle">
          Showing the 200 most recent matches — the cards above still count every return. Narrow the
          date range or a filter to reach older ones.
        </div>
      )}

      {/* Row click opens the detail in a right-side drawer (no page nav). Approve/reject/withdraw from within
          it refresh the list; a terminal action (reject/withdraw) closes the drawer. */}
      <Modal
        open={!!revertFor}
        onOpenChange={(o) => { if (!o) { setRevertFor(null); setRevertReason(''); } }}
        title={`Ask to reopen ${revertFor?.returnNo ?? 'this return'}`}
        footer={
          <>
            <Button variant="default" onClick={() => { setRevertFor(null); setRevertReason(''); }}>Cancel</Button>
            <Button variant="primary" loading={requestRevert.isPending}
              disabled={revertReason.trim().length < 10}
              onClick={() => requestRevert.mutate()}>Send to Approvals</Button>
          </>
        }
      >
        <p className="mb-3 type-body text-muted">
          This does not reopen the return. It puts the question in Approvals for a Super Admin, who is
          the only role that can undo a decision. Your reason is what they act on — and it becomes the
          reason recorded against the reversal.
        </p>
        <Input.TextArea
          rows={3}
          maxLength={500}
          value={revertReason}
          onChange={(e) => setRevertReason(e.target.value)}
          placeholder="e.g. Rejected in error — the dealer did return the goods, warehouse confirmed receipt on 02 Sep"
        />
        <p className="mt-1 type-caption text-subtle">At least 10 characters.</p>
      </Modal>

      {/* A centred dialog, not a slide-over: the panel carries a summary grid, a lines table and an
          audit trail, which a 620px side panel could only show by scrolling. */}
      <Modal
        title="Return details"
        width={980}
        open={!!openId}
        onOpenChange={(o) => { if (!o) closeDrawer(); }}
        footer={<Button variant="default" onClick={closeDrawer}>Close</Button>}
      >
        {openId && <ReturnDetailPanel id={openId} onClosed={closeDrawer} />}
      </Modal>
    </div>
  );
}
